import { useSlideImagePageStyles } from 'components/pages/styles';
import React, { useEffect } from 'react';
import { SlideImageView } from 'components/ImageViewer/SlideImageView';
import {
  clearSlidesList,
  setIsSharedPage,
  setSlidesListOrder,
} from 'store/slices/slidesListSlice/slidesListSlice';
import { MiniDrawerWithContext } from 'components/Layout/drawer';
import { useRouteMatch } from 'react-router-dom';
import { setSlides, slidesSelector } from 'store/slices/slidesSlice';
import { useDispatch, useSelector } from 'react-redux';
import { getSlideViewerPageTitle } from 'components/OrderSlidesList/utilities';
import { useTitle } from 'components/utilities/hooks/useTitle';
import { FETCH_SLIDE_ERROR_MESSAGE } from 'constants/errorMessages';
import { useSnackbar } from 'utilities/hooks/useSnackbar/useSnackbar';
import {
  useLazyGetSharedSlideQuery,
  useLazyGetSlideQuery,
} from 'store/apis/slidesApi';
import { currentUserPreferencesSelector } from 'store/slices/userDetailsSlice';
import { ANNOTATION_UX_V2_FIELD } from 'components/ImageViewer/constants';
import {
  SLIDE_VIEWER_COMPACT_LAYOUT_VARS,
  SlideViewerAppBarActions,
  SlideViewerAppBarTitle,
} from 'components/ImageViewer/components/SlideViewerAppBar/SlideViewerAppBar';

export const SlideImagePage = () => {
  const match = useRouteMatch();
  const slides = useSelector(slidesSelector);
  const dispatch = useDispatch();
  const { classes } = useSlideImagePageStyles();
  const slide = slides[0];
  const { showError } = useSnackbar();
  const preferences = useSelector(currentUserPreferencesSelector);
  // The contextual annotation UX also folds the header rows into the app bar.
  const isCompactHeader = !!preferences[ANNOTATION_UX_V2_FIELD];

  const { slideUUID: routeSlideUUID, shareSlideUUID } = match.params;
  const slideUUID = routeSlideUUID ?? shareSlideUUID;

  const [getSlide] = useLazyGetSlideQuery();
  const [getSharedSlide] = useLazyGetSharedSlideQuery();

  const pageTitle = getSlideViewerPageTitle(slide);
  useTitle(pageTitle);

  useEffect(() => {
    let isMounted = true;

    if (!slideUUID) {
      return;
    }

    dispatch(setIsSharedPage(!!shareSlideUUID));

    const slideGetter = shareSlideUUID
      ? getSharedSlide({ shareUUID: slideUUID }).unwrap()
      : getSlide({ slideUUID }).unwrap();

    slideGetter
      .then((fetchedSlide) => {
        if (!isMounted) {
          return;
        }

        dispatch(setSlides([fetchedSlide]));
        dispatch(setSlidesListOrder(fetchedSlide.order));
      })
      .catch(() => showError(FETCH_SLIDE_ERROR_MESSAGE));

    return () => {
      dispatch(clearSlidesList());
      isMounted = false;
    };
  }, [slideUUID]);

  return (
    <MiniDrawerWithContext
      header={
        isCompactHeader ? (
          <SlideViewerAppBarTitle slide={slide} />
        ) : (
          'Slide Viewer'
        )
      }
      headerActions={
        isCompactHeader && slide ? <SlideViewerAppBarActions /> : null
      }
      compactContent={isCompactHeader}
      withoutBackground
    >
      <div
        className={classes.viewer}
        style={isCompactHeader ? SLIDE_VIEWER_COMPACT_LAYOUT_VARS : undefined}
      >
        {slide && <SlideImageView slide={slide} />}
      </div>
    </MiniDrawerWithContext>
  );
};
